import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withNamespaces } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Container, Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import Spinner from 'react-bootstrap/Spinner';

import Navigator from "src/components/Navigator";
import ThirdpartyUsersService from 'src/modules/3rd-party-management/apis/common/ThirdpartyUsersService';
import SuccessModal from '../new/components/SuccessModal';
import InsufficientLicenseErrorModal from '../new/components/InsufficientLicenseErrorModal';
import ThirdPartyAiForm from './components/ThirdPartyAiForm';
import CreateConfirmModal from './components/CreateConfirmModal';
import useThirdPartyAiForm from './hooks/useThirdPartyAiForm';
import useAiCompanyInfo from './hooks/useAiCompanyInfo';

const NewThirdPartyAiCreate = ({
    t,
    i18n,
    user,
    organization,
}) => {
    const [hasLicence, setHasLicence] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [existenceErrorModal, setExistenceErrorModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const handleCheckLicenceExistence = useMutation({
        mutationFn: async () => {
            const service = ThirdpartyUsersService.getInstance();
            return await service.checkThirdpartyLicenceExistence();
        },
        onSuccess: () => {
            setHasLicence(true);
        },
        onError: (error) => {
            if (error?.response?.data?.error === 'insufficient_license') {
                setHasLicence(false);
                setExistenceErrorModal(true);
            } else {
                toast(t('Checking the existense of licences failed.'), {
                    type: 'error',
                });
            }
        },
    });

    const countriesQuery = useQuery({
        queryKey: ['3rd-party-management-ai-create-countries-list'],
        queryFn: async () => {
            const service = ThirdpartyUsersService.getInstance();
            return await service.fetchCountries();
        },
        cacheTime: 0,
        enabled: hasLicence,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error',
            });
        },
    });

    const memoizedCountriesList = useMemo(() => {
        return (countriesQuery.data || []).map((country) => ({
            value: country.id,
            baseLabel: country.name,
            label: t(country.name),
            code: country.code,
        }));
    }, [countriesQuery.data, t]);

    const {
        formValues,
        aiFieldsVisible,
        updateField,
        applyAiData,
        submitForm,
        isSubmitting,
        validateForm,
    } = useThirdPartyAiForm({
        t,
        user,
        organization,
        countriesList: memoizedCountriesList,
    });

    const {
        isLoading: isAiLoading,
        canGenerate,
        generateWithAi,
        refreshAiData,
    } = useAiCompanyInfo({
        t,
        values: formValues,
        onSuccess: applyAiData,
    });

    useEffect(() => {
        handleCheckLicenceExistence.mutate();
    }, []);

    const handleConfirmCreate = () => {
        submitForm({
            onSuccess: () => {
                setShowConfirmModal(false);
                setOpenSuccessModal(true);
            },
            onError: ({ response } = {}) => {
                const { data } = response || {};

                if (data?.error === 'insufficient_license') {
                    toast(t(
                        'You have reached the maximum number of third parties allowed by your license. Creating additional third parties is not possible. Please contact support if you need to increase your limit.'
                    ), {
                        type: 'error',
                    });
                } else if (data?.error === 'miss token') {
                    toast(t('Your session has expired or the authentication token is missing. Please reload the page and log in again.'), {
                        type: 'error',
                    });
                } else if (data?.message === 'There was a validation error') {
                    toast(t('There was a validation error. Please check your input and try again.'), {
                        type: 'error',
                    });
                } else {
                    toast(t('An error occurred while creating third party.'), {
                        type: 'error',
                    });
                }
            },
        });
    };

    return (
        <div className="page-content new-thirdparty-page ai-create-thirdparty-page">
            {openSuccessModal && (
                <SuccessModal
                    supplierType={formValues.type}
                    setModal={setOpenSuccessModal}
                    modal={openSuccessModal}
                />
            )}
            {existenceErrorModal && (
                <InsufficientLicenseErrorModal
                    supplierType={formValues.type}
                    setModal={setExistenceErrorModal}
                    modal={existenceErrorModal}
                />
            )}
            <CreateConfirmModal
                isOpen={showConfirmModal}
                toggle={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmCreate}
                isSubmitting={isSubmitting}
                thirdPartyName={formValues.name}
            />

            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator pageTitle={t('New Third Party')} translate={false} />
                    </Col>
                </Row>

                <Row>
                    <Col sm="12">
                        {handleCheckLicenceExistence.isLoading || countriesQuery.isFetching ? (
                            <div className="d-flex justify-content-center align-items-center">
                                <Spinner size="sm" className="me-1" color="primary" />
                                {t('Please wait')}...
                            </div>
                        ) : (
                            <ThirdPartyAiForm
                                t={t}
                                lng={i18n.language}
                                values={formValues}
                                countriesList={memoizedCountriesList}
                                aiFieldsVisible={aiFieldsVisible}
                                isAiLoading={isAiLoading}
                                canGenerate={canGenerate}
                                isSubmitting={isSubmitting}
                                onFieldChange={updateField}
                                onGenerateWithAi={generateWithAi}
                                onRefreshAi={refreshAiData}
                                onCreateClick={() => {
                                    if (validateForm()) {
                                        setShowConfirmModal(true);
                                    }
                                }}
                            />
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

const mapStatetoProps = (state) => ({
    user: state.Login.user,
    organization: state.Organization,
});

export default withNamespaces()(
    withRouter(connect(mapStatetoProps, null)(NewThirdPartyAiCreate))
);
